import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageField } from "@/components/admin/ImageField";

const CATEGORIES = ["Basketball", "Football", "Soccer / Football", "Track & Field", "Combat", "Motorsports", "General"];
const EMPTY = { title: "", category: "Basketball", excerpt: "", cover_url: "", read_time: "4 MIN READ", body: "", featured: false, published_date: "", author: "Ekal Desk" };

export const ArticlesTab = () => {
  const [articles, setArticles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, published_date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title,
      category: a.category,
      excerpt: a.excerpt,
      cover_url: a.cover_url,
      read_time: a.read_time,
      body: a.body,
      featured: !!a.featured,
      published_date: (a.created_at || "").slice(0, 10),
      author: a.author || "Ekal Desk",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/articles/${editing.id}`, form);
        toast.success("Article updated.");
      } else {
        await api.post("/articles", form);
        toast.success("Article published.");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Article deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const field =
    "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none";

  return (
    <div data-testid="admin-articles-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
          {articles.length} stories on the wire
        </p>
        <button
          data-testid="admin-article-create-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#FF3B30] hover:bg-red-600 text-white text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
        >
          <Plus size={14} /> New Article
        </button>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {articles.map((a) => (
          <div key={a.id} data-testid={`admin-article-row-${a.id}`} className="flex items-center gap-4 p-4 bg-[#12151C] hover:bg-[#181C26] transition-colors">
            <img src={a.cover_url} alt="" className="w-16 h-12 object-cover border border-white/10 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold uppercase tracking-tight text-base truncate">{a.title}</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                {a.category} · {a.read_time}
                {a.featured && <span className="text-amber-400"> · Featured</span>}
              </p>
            </div>
            <button
              data-testid={`admin-article-edit-${a.id}`}
              onClick={() => openEdit(a)}
              className="p-2 text-slate-400 hover:text-amber-400 transition-colors"
              aria-label="Edit"
            >
              <Pencil size={16} />
            </button>
            <button
              data-testid={`admin-article-delete-${a.id}`}
              onClick={() => remove(a)}
              className="p-2 text-slate-400 hover:text-[#FF3B30] transition-colors"
              aria-label="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {articles.length === 0 && (
          <p className="p-8 text-center text-xs font-mono uppercase tracking-[0.2em] text-slate-500">No articles yet.</p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="article-form-modal" className="bg-[#12151C] border-white/15 text-slate-100 rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold uppercase tracking-tight text-2xl">
              {editing ? "Edit Article" : "New Article"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-2">
            <Input
              data-testid="article-form-title"
              required
              placeholder="Headline"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={field}
            />
            <Input
              data-testid="article-form-author"
              placeholder="Filed by — author name (shown on the story)"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className={field}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <select
                data-testid="article-form-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-10 w-full bg-[#0A0C10] border border-white/15 px-3 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
              >
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <Input
                data-testid="article-form-read-time"
                placeholder="Read time (e.g. 5 MIN READ)"
                value={form.read_time}
                onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                className={field}
              />
              <div>
                <Input
                  data-testid="article-form-date"
                  type="date"
                  value={form.published_date}
                  onChange={(e) => setForm({ ...form, published_date: e.target.value })}
                  className={`${field} [color-scheme:dark]`}
                  title="Publish date — controls the order on the site (newest first)"
                />
              </div>
            </div>
            <ImageField
              label="Cover image — paste a link or pick from your library"
              value={form.cover_url}
              onChange={(v) => setForm({ ...form, cover_url: v })}
              photos={photos}
              testid="article-form-cover"
              required
            />
            <Textarea
              data-testid="article-form-excerpt"
              required
              rows={2}
              placeholder="Standfirst / excerpt"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              className={field}
            />
            <Textarea
              data-testid="article-form-body"
              required
              rows={8}
              placeholder="Body — separate paragraphs with a blank line"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className={field}
            />
            <label className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
              <input
                data-testid="article-form-featured"
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="accent-[#FF3B30] h-4 w-4"
              />
              Feature on coverage grid
            </label>
            <button
              data-testid="article-form-save-btn"
              type="submit"
              disabled={saving}
              className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Publish"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
